import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppBranding } from '../../../../config/branding';
import DefaultApiLimit from '../../../../config/defaultLimit';
import {
  AvailableRevenuePointReport,
  RequestReportInterFace,
} from '../../../../interface/reportrevenuePoint.interface';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import {
  fetchRevenueReportPoint,
  updateReportFilters,
  fetchListFilters,
  setReportFiltersData,
  setRevenuePointReportUpdate,
  setSortData,
} from '../../../../redux/slices/ReportRevenuePointSlice';
import { resetReportUpdateFlag } from '../../../../redux/slices/ReportSlice';
import CustomTable from '../../../../shared-components/custom-table/CustomTable';
import StationSidePanel from '../../../../shared-components/table-side-panel/StationSidePanel';
import { GetParametersFromCommonFilters } from '../../../../shared-functions/CommonFilters';
import { exportCSV, exportPDF } from '../../../../shared-functions/ExportFile';
import ToFixedValue from '../../../../shared-functions/ToFixedValue';
import reportApi from '../../../../utils/api/reports';

interface ReportTableProperties {
  leftTopContent?: JSX.Element;
  preferenceFilter: any;
}
/**
 * EnergyReportTablePoint component
 */
function RevenueReportTablePoint(properties: ReportTableProperties) {
  const branding = useAppBranding();
  const { t } = useTranslation('report');
  const { leftTopContent, preferenceFilter } = properties;

  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const {
    sortBy,
    sortField,
    reports,
    limit,
    page,
    loading,
    totalPages,
    totalResults,
    filters,
    filterListLoading,
    isFilterUpdated,
    isRevenuePointReportUpdate,
  } = useAppSelector((state) => state.reportRevenuePoint);
  const dispatch = useAppDispatch();
  const reportsData = useAppSelector((state) => state.reportData);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);

  const column = [
    {
      label: t('chargingPoint'),
      accessorKey: 'chargingPointSn' as keyof AvailableRevenuePointReport,
    },
    {
      label: t('stationInfo'),
      accessorKey: 'chargingStationName' as keyof AvailableRevenuePointReport,
      /**
       * S.No view
       * @param station
       * @param index
       */
      customData: (report: AvailableRevenuePointReport) => (
        <div className="table-row-each-data">
          <div className="station-info-left">
            <span role="button" tabIndex={0}>
              {report.chargingStationName}
            </span>
            <span>{report.date}</span>
          </div>
          <div className="station-info-right">
            <span role="button" tabIndex={0}>
              {report.chargingStationLocation}
            </span>
          </div>
        </div>
      ),
      queryParamsKey: 'stationId',
      queryParamsValue: 'chargingStation',
      sidePanelComponent: <StationSidePanel fetchIdFrom="queryParams" />,
      sortAllow: false,
      columnWidth: 204,
    },

    {
      label: t('totalRevenue'),
      accessorKey: 'totalRevenue' as keyof AvailableRevenuePointReport,
      sortAllow: true,
      /**
       * decimal value round of
       * @param row
       * @returns
       */
      customData: (row: AvailableRevenuePointReport) => (
        <div className="value-container">
          <p className="value">{ToFixedValue(row?.totalRevenue)}</p>
          <span>{branding.text.currency}</span>
        </div>
      ),
    },
    {
      label: t('basePricingRevenue'),
      accessorKey: 'basePriceRevenue' as keyof AvailableRevenuePointReport,
      sortAllow: true,
      /**
       * decimal value round of
       * @param row
       * @returns
       */
      customData: (row: AvailableRevenuePointReport) => (
        <div className="value-container">
          <p className="value">{ToFixedValue(row?.basePriceRevenue)}</p>
          <span>{branding.text.currency}</span>
        </div>
      ),
    },
    {
      label: t('peakPricingRevenue'),
      accessorKey: 'peakPriceRevenue' as keyof AvailableRevenuePointReport,
      sortAllow: true,
      /**
       * decimal value round of
       * @param row
       * @returns
       */
      customData: (row: AvailableRevenuePointReport) => (
        <div className="value-container">
          <p className="value">{ToFixedValue(row?.peakPriceRevenue)}</p>
          <span>{branding.text.currency}</span>
        </div>
      ),
    },
    {
      label: t('offPeakPricingRevenue'),
      accessorKey: 'offPeakPriceRevenue' as keyof AvailableRevenuePointReport,
      sortAllow: true,
      /**
       * decimal value round of
       * @param row
       * @returns
       */
      customData: (row: AvailableRevenuePointReport) => (
        <div className="value-container">
          <p className="value">{ToFixedValue(row?.offPeakPriceRevenue)}</p>
          <span>{branding.text.currency}</span>
        </div>
      ),
    },
    {
      label: t('scheduleChargeRevenue'),
      accessorKey: 'scheduleChargeRevenue' as keyof AvailableRevenuePointReport,
      sortAllow: true,
      /**
       * decimal value round of
       * @param row
       * @returns
       */
      customData: (row: AvailableRevenuePointReport) => (
        <div className="value-container">
          <p className="value">{ToFixedValue(row?.scheduleChargeRevenue)}</p>
          <span>{branding.text.currency}</span>
        </div>
      ),
    },
  ];
  /**
   * Fetches energy report data for the selected facility.
   * @param data - The request parameters for fetching the report.
   */
  const fetchData = (data: RequestReportInterFace) => {
    if (!loading) {
      dispatch(
        fetchRevenueReportPoint({
          ...data,
          accountCustomId: reportsData?.accountId,
        }),
      );
    }
  };

  /**
   * Current columns for Revenue Point Report export
   */
  const currentColumns = [
    { label: 'SNo', accessorKey: '__serial__' },
    { label: t('date'), accessorKey: 'date' },
    { label: t('stationName'), accessorKey: 'chargingStationName' },
    { label: t('stationLocation'), accessorKey: 'chargingStationLocation' },
    { label: t('totalRevenue'), accessorKey: 'totalRevenue' },
    { label: t('basePricingRevenue'), accessorKey: 'basePriceRevenue' },
    { label: t('peakPricingRevenue'), accessorKey: 'peakPriceRevenue' },
    { label: t('offPeakPricingRevenue'), accessorKey: 'offPeakPriceRevenue' },
    { label: t('scheduleChargeRevenue'), accessorKey: 'scheduleChargeRevenue' },
  ];

  const exportCurrentColumns = currentColumns.filter((col) => {
    // Always show serial number
    if (col.accessorKey === '__serial__') return true;

    // 🔥 If chargingStationName is hidden,
    // automatically hide related columns
    const isParentHidden = hiddenColumns.includes('chargingStationName');

    if (isParentHidden) {
      if (
        col.accessorKey === 'chargingStationName' ||
        col.accessorKey === 'date' ||
        col.accessorKey === 'chargingStationLocation'
      ) {
        return false;
      }
    }

    // Default hide logic
    return !hiddenColumns.includes(col.accessorKey);
  });

  /**
   * Export data function
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);

    const queryParameters = GetParametersFromCommonFilters(filters);

    const exportParameters: any = {
      ...queryParameters,
      startDate: reportsData.reportFilter.startDate,
      endDate: reportsData.reportFilter.endDate,
    };

    if (sortBy) {
      exportParameters.sortBy = sortBy;
      exportParameters.sortField = sortField;
    }

    reportApi
      .fetchRevenuePointListExport({
        ...exportParameters,
        accountCustomId: reportsData?.accountId,
      })
      .then((response) => {
        /**
         * Dynamic Headers
         */
        const headers = exportCurrentColumns.map((col) => col.label);

        /**
         * Dynamic Rows
         */
        const rows = response.data.map((row: AvailableRevenuePointReport, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;

              case 'date':
                return row?.date || '-';

              case 'chargingStationName':
                return row?.chargingStationName || '-';

              case 'chargingStationLocation':
                return row?.chargingStationLocation || '-';

              case 'totalRevenue':
                return row?.totalRevenue
                  ? `${ToFixedValue(row.totalRevenue)} ${branding.text.currency}`
                  : `0.00 ${branding.text.currency}`;

              case 'basePriceRevenue':
                return row?.basePriceRevenue
                  ? `${ToFixedValue(row.basePriceRevenue)} ${branding.text.currency}`
                  : `0.00 ${branding.text.currency}`;

              case 'peakPriceRevenue':
                return row?.peakPriceRevenue
                  ? `${ToFixedValue(row.peakPriceRevenue)} ${branding.text.currency}`
                  : `0.00 ${branding.text.currency}`;

              case 'offPeakPriceRevenue':
                return row?.offPeakPriceRevenue
                  ? `${ToFixedValue(row.offPeakPriceRevenue)} ${branding.text.currency}`
                  : `0.00 ${branding.text.currency}`;

              case 'scheduleChargeRevenue':
                return row?.scheduleChargeRevenue
                  ? `${ToFixedValue(row.scheduleChargeRevenue)} ${branding.text.currency}`
                  : `0.00 ${branding.text.currency}`;

              default:
                return '-';
            }
          }),
        );

        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'reports-list.csv');
          } else {
            exportPDF(headers, rows, 'reports-list.pdf');
          }
        }

        setExportLoader(false);
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 404,
              message: error?.response?.data?.message || 'Something went wrong!',
            },
          }),
        );
      });
  };

  /**
   * Reference Current Pages
   */
  const ReferenceCurrentPages = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      sortBy,
      sortField,
      startDate: reportsData.reportFilter.startDate,
      endDate: reportsData.reportFilter.endDate,
      year: reportsData.reportFilter.year,
      page,
      limit,
      accountCustomId: reportsData?.accountId,
    });
  };

  /**
   * Function to handle ApiRequest when page or limit changes
   * @param value
   */
  const handleApiRequest = (value: RequestReportInterFace) => {
    fetchData({
      ...GetParametersFromCommonFilters(filters),
      ...value,
      sortField,
      sortBy,
      startDate: reportsData.reportFilter.startDate,
      endDate: reportsData.reportFilter.endDate,
    });
  };
  /**
   * Handle the filter data clear function
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      startDate: reportsData.reportFilter.startDate,
      endDate: reportsData.reportFilter.endDate,
      page: DefaultApiLimit.page,
      limit,
    });
  };
  const fetchReportData = useCallback(() => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      startDate: reportsData.reportFilter.startDate,
      endDate: reportsData.reportFilter.endDate,
      year: reportsData.reportFilter.year,
      page: DefaultApiLimit.page,
      limit: DefaultApiLimit.limit,
    });
  }, [
    filters,
    reportsData.reportFilter.startDate,
    reportsData.reportFilter.endDate,
    reportsData.reportFilter.year,
  ]);

  useEffect(() => {
    if (isFilterUpdated || reports.length === 0 || isRevenuePointReportUpdate) {
      fetchReportData();
      dispatch(resetReportUpdateFlag());
      dispatch(setRevenuePointReportUpdate(false));
    }
  }, [fetchReportData, isFilterUpdated, reports.length, isRevenuePointReportUpdate]);

  useEffect(() => {
    if (
      preferenceFilter.pageName === 'revenueReport' &&
      filters.length > 1 &&
      reportsData.reportByType !== 'station'
    ) {
      const setFilter = filters.map((filter) => {
        let returnValue = filter;
        const single = preferenceFilter.filter?.find(
          (preference: any) => preference.field === filter.key,
        );
        if (single && single?.value.length > 0) {
          returnValue = {
            ...returnValue,
            value: single?.value,
            applyFilter: true,
            valueToShow: returnValue?.listOfValue
              ?.filter((show) => single?.value?.includes(show.value))
              .map((show) => show.label),
          };
        } else {
          returnValue = {
            ...returnValue,
            value: [],
            applyFilter: false,
            valueToShow: [],
          };
        }
        return returnValue;
      });
      dispatch(setReportFiltersData(setFilter));
      const queryParameters = GetParametersFromCommonFilters(setFilter);
      fetchData({
        ...queryParameters,
        startDate: reportsData.reportFilter.startDate,
        endDate: reportsData.reportFilter.endDate,
        page: DefaultApiLimit.page,
        limit,
      });
    }
  }, [preferenceFilter]);

  useEffect(() => {
    if (filters.length <= 1 && reportsData?.accountId) {
      dispatch(fetchListFilters({ accountCustomId: reportsData?.accountId }));
    }
  }, [reportsData?.accountId]);

  return (
    <div>
      <CustomTable
        disablePagination={false}
        loading={loading}
        limit={limit}
        header={column}
        data={reports}
        page={page}
        totalPages={totalPages}
        totalResults={totalResults}
        sortData={{ sortBy, sortField }}
        handleSortChange={handleSortFunction}
        pageLimitChange={handleApiRequest}
        version="v2"
        exportDataPdfOrCSV={exportDataPdfOrCSV}
        exportData={reports}
        exportLoader={exportLoader}
        disableHeader={false}
        height={window.innerHeight >= 700 ? 320 : 339}
        filters={filters}
        filtersToDispatch={updateReportFilters}
        filterListLoading={filterListLoading}
        filterListToDispatch={fetchListFilters}
        leftTopContent={leftTopContent}
        ReferenceCurrentPages={ReferenceCurrentPages}
        sizeChangeButtonRequired
        serialNo
        onHiddenColumnsChange={setHiddenColumns}
      />
    </div>
  );
}

export default RevenueReportTablePoint;
